import { FC, useMemo } from "react";
import { AmtTransferDataItem } from "../../../../services/getAmtTransfer";
import { useStyle } from "./TransferFund.styles";
import { toast } from "react-toastify";
import { Button, Modal, Text, Group, Stack } from "@mantine/core";
import {
  MRT_Cell,
  MRT_ColumnDef,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import useGetAmtTransfer from "../../../../hooks/useGetAmtTransferMutation";
import { useState, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import useTransferFundToJVA from "../../../../hooks/useTransferFundToJVAConfirmed";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formatNumbersWithThousandSeperators } from "@funded-here-interface/common/src/Utils/formatter";

interface Props {
  id: string;
  structuredNoteDisplayId: string;
}

const TransferFund: FC<Props> = ({ id, structuredNoteDisplayId }) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const { classes } = useStyle();
  const [data, setData] = useState<AmtTransferDataItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [amtTransferInput, setAmtTransferInput] = useState<number>();
  const amtTransferMutation = useGetAmtTransfer();
  const [initialAmtToTransfer, setInitialAmtToTransfer] = useState(0);
  const [totalAmtTransferred, setTotalAmtTransferred] = useState<number>(0);
  const [updatedTotalAmtInvest, setUpdatedTotalAmtInvest] = useState(0);
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [openedTransfer, modalAction] = useDisclosure(false);
  const transferFundConfirmed = useTransferFundToJVA();
  const [transferBtndisabled, setTransferBtndisabled] = useState(false);
  const [currency, setCurrency] = useState<string>("");
  const [error, setError] = useState({ amount_error: "" });
  const isTwoDecimal = true;
  const calculateTotalInvest = (
    data: AmtTransferDataItem[],
    totalToCount: string
  ) => {
    let total = 0;
    if (totalToCount === "amountToTransfer") {
      for (const item of data) {
        total += item.amountToTransfer;
        total = parseFloat(total.toFixed(2));
      }
    }
    if (totalToCount === "requiredTransferAmt") {
      for (const item of data) {
        total += item.requiredTransferAmt;
        total = parseFloat(total.toFixed(2));
      }
    }
    if (totalToCount === "amountTransferred") {
      for (const item of data) {
        total += item.amountTransferred;
        total = parseFloat(total.toFixed(2));
      }
    }
    return total;
  };
  const [maximumBalAmtToTransfer, setMaximumBalAmtToTransfer] =
    useState<number>(0);

  const handleTransferClick = () => {
    setIsTransferLoading(true);
    modalAction.open();
    setIsTransferLoading(false);
  };

  const handleTransferConfirmClick = async () => {
    const merchantTransferTemp = data
      .filter((x) => x.amountToTransfer !== 0)
      .map((merchant) => ({
        merchantOrgName: merchant.merchantOrgName,
        merchantOrgRoleId: merchant.merchantOrgRoleId,
        amountToTransfer: merchant.amountToTransfer,
        currency: merchant.currency,
        structuredNoteId: Number(id),
        structuredNoteDisplayId: structuredNoteDisplayId,
      }));
    transferFundConfirmed.mutate(
      {
        token: token,
        merchantTransfer: merchantTransferTemp,
      },
      {
        onSuccess: () => {
          toast.info("Funds have been successfully transferred.");
          fetchData();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          fetchData();
        },
        onSettled: () => {
          modalAction.close();
        },
      }
    );
  };

  const fetchData = async () => {
    amtTransferMutation.mutate(
      {
        token,
        id,
      },
      {
        onSuccess: (data) => {
          setMaximumBalAmtToTransfer(
            calculateTotalInvest(data.amtTransferResult, "requiredTransferAmt")
          );
          setData(data.amtTransferResult || []);
          setTransferBtndisabled(false);
          setInitialAmtToTransfer(
            calculateTotalInvest(data.amtTransferResult, "amountToTransfer")
          );
          setUpdatedTotalAmtInvest(
            calculateTotalInvest(data.amtTransferResult, "amountToTransfer")
          );
          setTotalAmtTransferred(
            calculateTotalInvest(data.amtTransferResult, "amountTransferred")
          );
          setAmtTransferInput(0);
          setCurrency(data.amtTransferResult[0].currency);
          setIsLoading(false);
        },
        onError: (error) => {
          setTransferBtndisabled(true);
          setIsLoading(false);
          toast.error((error as Error).message);
        },
      }
    );
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const isNumberWithTwoDecimal = /^(\d+|\d*\.\d{1,2})$/.test(value);
    const positiveNumber = Number(value) > 0;
    if (!isNumberWithTwoDecimal || !positiveNumber) {
      setError((prevError) => ({
        ...prevError,
        amount_error:
          "Amount to be transferred must a positive number with at maximum 2 decimal places.",
      }));
    } else {
      const parsedValue = parseFloat(value);
      setError((prevError) => ({ ...prevError, amount_error: "" }));
      setAmtTransferInput(parsedValue);
    }
  };

  const blockInvalidCharNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    const cursorPos = e.currentTarget.selectionStart ?? 0;
    const key = e.key;
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
      return;
    }
    if (key.length > 1 || e.ctrlKey || e.metaKey) {
      return;
    }
    const simulated = value.slice(0, cursorPos) + key + value.slice(cursorPos);

    const match = simulated.match(/^\d*(\.(\d{0,2})?)?$/);

    if (!match) {
      e.preventDefault(); // block if it would exceed 2 decimals
    }
  };

  const handleSaveCell = (cell: MRT_Cell<AmtTransferDataItem>, value: any) => {
    const updatedData = [...data];
    updatedData[cell.row.index] = {
      ...updatedData[cell.row.index],
      [cell.column.id as keyof AmtTransferDataItem]: Number(value),
    };
    setData(updatedData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const total = parseFloat(
      data.reduce((acc, x) => acc + x.amountToTransfer, 0).toFixed(2)
    );
    setUpdatedTotalAmtInvest(total);
    if (updatedTotalAmtInvest <= 0) {
      setTransferBtndisabled(true);
    }
  }, [data, updatedTotalAmtInvest]);

  useEffect(() => {
    if (updatedTotalAmtInvest > initialAmtToTransfer) {
      toast.error(
        "Amount to be transferred exceeds the maximum available balance for junior investment tranche."
      );
      setTransferBtndisabled(true);
      return;
    } else if (updatedTotalAmtInvest <= 0) {
      setTransferBtndisabled(true);
    } else {
      setTransferBtndisabled(false);
    }
  }, [initialAmtToTransfer, updatedTotalAmtInvest]);

  const columns = useMemo<MRT_ColumnDef<AmtTransferDataItem>[]>(
    () => [
      {
        accessorKey: "merchantOrgName",
        header: "Merchant Name",
        enableEditing: false,
      },
      {
        accessorKey: "currency",
        header: "Currency",
        enableEditing: false,
        size: 50,
      },
      {
        accessorKey: "requiredTransferAmt",
        header: "Required Transfer Amount",
        enableEditing: false,
        Cell: ({ cell }) => <span>{cell.getValue<number>().toFixed(2)}</span>,
      },
      {
        accessorKey: "amountTransferred",
        header: "Amount Transferred",
        enableEditing: false,
        Cell: ({ cell }) => <span>{cell.getValue<number>().toFixed(2)}</span>,
      },
      {
        accessorKey: "amountToTransfer",
        header: "Amount to Be Transferred (double-click to edit)",
        enableHTML: true,
        headerStyle: { fontStyle: "italic" },
        Cell: ({ cell }) => <span>{cell.getValue<number>().toFixed(2)}</span>,
        mantineEditTextInputProps: {
          error: error.amount_error,
          required: true,
          type: "text",
          inputMode: "decimal",
          onChange: handleChange,
          onKeyDown: blockInvalidCharNumber,
        },
      },
    ],
    [data]
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: false,
    paginationDisplayMode: "pages",
    state: { isLoading },
    columns,
    data: data,
    initialState: { density: "xs" },
    mantineTableProps: {
      withBorder: true,
    },
    editDisplayMode: "cell",
    enableEditing: true,
    mantineEditTextInputProps: ({ cell }) => ({
      onBlur: (event: any) => {
        handleSaveCell(cell, event.target.value);
      },
    }),
    renderTopToolbarCustomActions: () => (
      <Stack spacing="md" px="1x">
        <Text fw="bold">
          Maximum Balance Available for FundedHere to Invest as a Junior
          Investor: {currency}{" "}
          {formatNumbersWithThousandSeperators(
            Number(maximumBalAmtToTransfer),
            isTwoDecimal
          )}
        </Text>
        <Text fw="bold">
          Total Amount Transferred from Merchant QC VA(s) to FH Junior Investor
          VA: {currency}{" "}
          {formatNumbersWithThousandSeperators(
            Number(totalAmtTransferred),
            isTwoDecimal
          )}
        </Text>
        <Text fw="bold">
          Total Amount to Be Transferred from Merchant QC VA(s) to FH Junior
          Investor VA: {currency}{" "}
          {formatNumbersWithThousandSeperators(
            Number(updatedTotalAmtInvest),
            isTwoDecimal
          )}
        </Text>
      </Stack>
    ),
  });

  return (
    <div>
      <div>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={() => handleTransferClick()}
            disabled={transferBtndisabled}
            style={{ backgroundColor: "rgba(51, 143, 134, 1)" }}
            loading={isTransferLoading}
          >
            Transfer Fund
          </Button>
        </div>

        <MantineReactTable table={table} />
      </div>
      <Modal opened={openedTransfer} onClose={modalAction.close} size="lg">
        <Text
          ta="center"
          style={{
            color: "#338F86",
            fontWeight: "bold",
            fontSize: "22px",
          }}
        >
          Funds Transfer Confirmation
        </Text>
        <Text fz="sm" ta="center" mt="lg">
          You are about to transfer {currency}{" "}
          {formatNumbersWithThousandSeperators(
            Number(updatedTotalAmtInvest),
            isTwoDecimal
          )}{" "}
          from the qualifying capital virtual account(s) of the merchant(s) to
          FundedHere junior investor's virtual account.
        </Text>
        <Text fz="sm" ta="center" mt="lg">
          Do you want to proceed?
        </Text>
        <Group mt="xl" position="center">
          <Button
            onClick={modalAction.close}
            style={{
              backgroundColor: "rgb(250, 250, 250)",
              border: "true",
              color: "gray",
              borderColor: "gray",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleTransferConfirmClick()}
            style={{ backgroundColor: "rgba(51, 143, 134, 1)" }}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default TransferFund;
